// © Microsoft Corporation. All rights reserved.

import { ConnectorConstants, IActionPayload, IDataConnector, IReducer } from "@msnews/experiences-redux-contracts";
import { assign, get, merge, set } from "lodash-es";

import { ConnectorNamespaces } from "@msnews/experiences-constants";
import { canUseDOM } from "@msnews/isomorphic-util";
import { dataConnectorInjector } from "./DataConnectorInjector";
import { isNullOrUndefined } from "util";
import { logError } from "@msnews/page-error-handling";
import { reportDataConnectorStateChange } from "./DataConnectorTracker";

/** Resolve & Reject Function Signature */
type ResolveConnectorSignature = (connector: IDataConnector<any, any>) => void;
type RejectConnectorSignature = (error: Error) => void;

/**
 * Pending getDataConnector Promise
 */
interface PendingConnectorPromise {
    /** Promise */
    promise: Promise<IDataConnector<any, any>>;
    /** callback to resolve the promise */
    resolve: ResolveConnectorSignature;
    /** callback to reject the promise */
    reject: RejectConnectorSignature;
}

/** Result from running a reducer. */
interface ReducerResult {
    /** The new state calculated by the reducer */
    newState: any;

    /** True if the state changed. */
    stateChanged: boolean;
}

/**
 * A reducer that can dynamically add and remove individual sub-reducers
 * @class
 */
export class DynamicReducer {

    /**
     * A map of connectors, which maintain the sub-spaced state. Reducers and connectors are keyed by namespace
     */
    private readonly _connectorMap: {[key: string]: IDataConnector<any, any>} = {};

    /**
     * The list of global connectors. Used for fast lookup instead of iterating through _connectorMap  keys and toArray methods.
     */
    private readonly _globalConnectors: IDataConnector<any, any>[] = [];

    /**
     * The list of all connectors. Used for fast lookup instead of iterating through _connectorMap keys and toArray methods.
     */
    private readonly _allConnectors: IDataConnector<any, any>[] = [];

    /**
     * The list of all pending promises for the get data connector call. Used to resolve the promise when data connector is instantiated.
     */
    private readonly _getDataConnectorPendingPromises: Map<string, PendingConnectorPromise> = new Map();

    /**
     * Creates an instance of the `DynamicReducer` class.
     * @constructor
     * @param {string} type The action type.
     */
    constructor() {
        // bind the reduce method to this instance
        this.reduce = this.reduce.bind(this);
    }

    /**
     * Returns an array of connectors that have their reducers registered with this instance
     * @method
     * @returns {IDataConnector<any, any>} A simple arry of all connectors
     */
    public connectors(): IDataConnector<any, any>[] {
        return this._allConnectors;
    }

    /**
     * Returns an array of connectors that have their reducers registered with this instance
     * @method
     * @param {string} namespace The namespace of the connector to return, if it is in the map
     * @returns {IDataConnector<any, any> | undefined} A simple arry of all connectors
     */
    public connector(namespace: string): IDataConnector<any, any> | undefined {
        return (this._connectorMap[namespace]);
    }

    /**
     * Returns an array of connectors that have state that is global
     * @method
     * @returns {IDataConnector<any, any>} A simple arry of connectors that have global state
     */
    public globalConnectors(): IDataConnector<any, any>[] {
        return this._globalConnectors;
    }

    /**
     * Returns a promise which resolves immediately if the data connector is already present on the page,
     * otherwise resolves when the data connector is instantiated on the Page later in the lifecycle.
     * @method
     * @param namespace The namespace of data connector to return
     */
    public getDataConnector(namespace: ConnectorNamespaces): Promise<IDataConnector<any, any>> {
        // Check for data connector, if it is already present resolve immediately
        const dataConnector = this.connector(namespace);
        if (dataConnector) {
            return Promise.resolve(dataConnector);
        }

        // Check if we have an entry for the namespace in pending promise map, if present return the same promise
        const pendingPromise = this._getDataConnectorPendingPromises.get(namespace);
        if (pendingPromise) {
            return pendingPromise.promise;
        }

        // If the data connector is not on the page and has no entry in the pending promise map, create a new promise
        let resolveCallback: ResolveConnectorSignature;
        let rejectCallback: RejectConnectorSignature;
        const promise = new Promise<IDataConnector<any, any>>((resolve, reject) => {
            // Get the reference to the resolve & reject which can be called later when the data connector is instantiated on the Page
            resolveCallback = resolve;
            rejectCallback = reject;
        });

        // Add the promise to the pending promise map
        this._getDataConnectorPendingPromises.set(namespace, { promise: promise, resolve: resolveCallback, reject: rejectCallback });

        // Return the promise
        return promise;
    }

    /**
     * Produces the next state based on the given action. The state to be reduced is an aggregate of the reducers for all of the connectors
     * that have been added.
     * The overall state looks something like:
     * This will be the state for the redux-subspace for the connector. It is of the form:
     * {
     *      FOO: {
     *          _@STATE@_: {
     *              <state for subspace with namespace: FOO>
     *          },
     *          BAR: {
     *              _@STATE@_: {
     *                  <state for subspace with namespace: BAR, which is a subspace within the FOO subspace>
     *               },
     *          },
     *      }
     * }
     * @function
     * @param state {any} The previous state of the application. The application state, will contain one or more sub-states, which will be individually reduced
     * @param actionPayload {IActionPayload} The action detail to update the state with.
     * @returns {any} The next state for the application, containing the next state for each connector (a.k.a subspace)
     */
    public reduce(state: any, actionPayload: IActionPayload): any {
        // If the initial state is undefined, then create an empty object to start with
        if (isNullOrUndefined(state)) {
            state = {};
        }

        let newState = { ...state };
        let stateChanged = false;
        const connectorKey: string = actionPayload.key;

        // See if we need to reduce for a specific connector. This is the common case.
        if (actionPayload !== undefined && connectorKey !== undefined) {
            const typeIsString: boolean = typeof actionPayload.type === "string";
            if (typeIsString && actionPayload.type.endsWith(ConnectorConstants.renderAction)) {
                // If this is a special action, used to force a re-render, then we need to set stateChanged to true, and create a clone of the existing scoped state
                const statePropertyKey = connectorKey + "." + ConnectorConstants.connectorStateKey;
                const connectorState = assign({}, get(state, statePropertyKey, undefined));
                set(newState, statePropertyKey, connectorState);
                stateChanged = true;
            } else if (typeIsString && actionPayload.type.endsWith(ConnectorConstants.dataConnectorMergeInitAction)) {
                // This is an action meant to set up initial state when an experience injects data connector state into itself.
                const experienceFullNamespace: string = connectorKey;
                const dataConnectorName: ConnectorNamespaces = actionPayload.params[0];

                if (dataConnectorName) {
                    dataConnectorInjector.initializeExperience(newState, experienceFullNamespace, dataConnectorName);
                    stateChanged = true;
                }
            } else {
                const connector = this._connectorMap[connectorKey];
                if (connector) {
                    // If the connector doing the reducing is global, then create a deep copy of state, so that all the connected
                    // components think the state is new, and run their mappers. Connected components are subscribed to, and only see
                    // changed in their subspace portion of the state tree. The redux connect implementation uses strict equal comparison,
                    // meaning it consider the state change if it is a new object reference, even if all the properties are still the same
                    if (connector.isGlobal) {
                        newState = merge({}, newState);
                    }

                    const connectorReducerResult = this.computeNextStateForConnector(newState, connectorKey, actionPayload);
                    stateChanged = connectorReducerResult.stateChanged;

                    if (stateChanged) {
                        // Check to see if any experiences are including this data connector state. If so, update those too.
                        dataConnectorInjector.reportStateChange(newState, connectorKey, connectorReducerResult.newState);

                        if (canUseDOM()) {
                            reportDataConnectorStateChange(connectorKey, connectorReducerResult.newState);
                        }
                    }
                } else {
                    // If connector is not found in the map, throw error immediately during SSR.
                    if (!canUseDOM()) {
                        logError(`ConnectorMap for ${connectorKey} is undefined.`, 34006, {});
                        throw new Error("Reducer for: " + connectorKey + " must not return undefined.");
                    }
                }
            }
        } else {
            // Iterate over the full list of connectors calling each reducer. This is not the common case, and is typically done
            // for actions that come from Redux, during initialization
            Object.keys(this._connectorMap).forEach(key => {
                if (this.computeNextStateForConnector(newState, key, actionPayload).stateChanged) {
                    stateChanged = true;
                }
            });
        }

        // Return the newState, as modified by the reducer(s) if it was changed, otherwise return the original state
        if (stateChanged) {
            return newState;
        } else {
            return state;
        }
    }

    /**
     * Adds a sub reducer for a connector to the sub-reducer collection.
     * @method
     * @param {IDataConnector<TSubReducerState, TSubReducerParentState>} connector The connector to add.
     */
    public addSubReducerForConnector<TSubReducerState, TSubReducerParentState>(connector: IDataConnector<TSubReducerState, TSubReducerParentState>): void {
        if (!connector) {
            throw new Error("The parameter 'connector' cannot be null.");
        }

        // Add the sub-reducer and connector into their maps, based on the connector's namespace
        const key = connector.fullNamespace;
        this._connectorMap[key] = connector;

        // update the all and global connector fast lookup lists
        this._allConnectors.push(connector);

        if (connector.isGlobal) {
            this._globalConnectors.push(connector);
        }

        // Check if there is a pending promise for the data connector
        // DO NOT CHANGE namespace to fullnamespace, data connectors are supposed to be queried using just the namespace by design.
        if (this._getDataConnectorPendingPromises.has(connector.namespace)) {
            // Get the resolve callback for the pending promise and resolve it
            const pendingPromise = this._getDataConnectorPendingPromises.get(connector.namespace);

            try {
                pendingPromise.resolve(connector);
            } catch (error) {
                pendingPromise.reject(error);
            }

            // Delete the entry from the pending promise map
            this._getDataConnectorPendingPromises.delete(connector.namespace);
        }

        connector.store.dispatch({ key: key, type: "@@connector-view/INIT" });
    }

    /**
     * Helper method to compute the next state for a given connector. This method will get the connector's actual state
     * from the state tree, call the reducer, and then state the state in the passed in object that is to receive the new state
     * @param {any} state The current subspace (or root) state object, which will be updated
     * @param {string} key The key to the connector in the connector map
     * @param {IActionPayload} actionPayload The action to apply
     * @returns Information on how the connector state was changed
     */
    private computeNextStateForConnector(state: any, key: string, actionPayload: IActionPayload): ReducerResult {
        // Get the connector, so that we can call the reducer
        const connector = this._connectorMap[key];

        // Setup the key and get the actual state property for the connector. lodash uses dotted notation
        // so the key will be something like: namespace._@STATE@_
        const statePropertyKey = key + "." + ConnectorConstants.connectorStateKey;
        const connectorPreviousState = get(state, statePropertyKey, undefined);

        // Call the reducer for the connector, and validate that we received a state object back.
        const nextStateForConnector = this.subReduce(connector.reducer, connectorPreviousState, actionPayload);
        if (typeof nextStateForConnector === "undefined") {
            throw new Error("Reducer for: " + key + " must not return undefined.");
        }

        // If the state changed, then set the new connector state in the overall state object
        const connectorStateChanged: boolean = nextStateForConnector !== connectorPreviousState;
        if (connectorStateChanged) {
            set(state, statePropertyKey, nextStateForConnector);
        }

        return { newState: nextStateForConnector, stateChanged: connectorStateChanged };
    }

    /**
     * Calls the reducer that has been registered to handle the state associated with a specific subspace
     * @param {Reducer<TSubReducerState>} reducer The reducer to call
     * @param {TSubReducerState} state The state object for the subreducer
     * @param {IActionPayload} actionPayload The action to apply
     */
    private subReduce<TSubReducerState>(reducer: IReducer<TSubReducerState>, previousState: TSubReducerState, actionPayload: IActionPayload): TSubReducerState {
        const reducedState: TSubReducerState = reducer.reduce(previousState, actionPayload);
        if (reducedState !== previousState) {
            return reducedState;
        } else {
            return previousState;
        }
    }
}